import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

import { EvalDashboard } from './eval-dashboard.js';
import { useEvalDashboardData, useFailedEvals, useRescoreReview, useReviewDetail } from './use-eval-dashboard-data.js';

import type { EvalKindOption } from './components/eval-kind-toggle.js';

const searchSchema = z.object({
	fixtures: z.coerce.boolean().optional(),
	kind: z.enum(['training', 'production']).catch('training').default('training'),
});

export const Route = createFileRoute('/_authorization-gate/wizard/admin/eval/operator/$userId')({
	validateSearch: searchSchema,
	component: WizardAdminEvalOperatorPage,
	head: () => ({ meta: [{ title: 'Operator Eval • Wizard Admin • Cadstrom' }] }),
});

function WizardAdminEvalOperatorPage() {
	const { userId } = useParams({ from: '/_authorization-gate/wizard/admin/eval/operator/$userId' });
	const search = Route.useSearch();
	const fixturesMode = Boolean(search.fixtures);
	const activeKind = search.kind;
	const navigate = useNavigate();

	const { plans, operators, heatmapRows } = useEvalDashboardData({
		fixtures: fixturesMode,
		operatorIds: [userId],
		evalKind: activeKind,
	});
	const failedEvals = useFailedEvals(fixturesMode, activeKind);
	const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
	const selectedDetail = useReviewDetail({ reviewId: selectedReviewId, fixtures: fixturesMode });
	const rescoreMutation = useRescoreReview();

	const operator = operators.find((op) => op.userId === userId);
	const operatorName = operator?.displayLabel ?? userId;

	function handleRowClick(reviewId: string) {
		setSelectedReviewId(reviewId.length > 0 ? reviewId : null);
	}

	function handleRescore(reviewId: string) {
		void rescoreMutation.mutateAsync({ reviewId });
	}

	function handleKindChange(kind: EvalKindOption) {
		// The open detail belongs to the previous population's row list, so a
		// kind switch closes it rather than stranding a row that is no longer shown.
		setSelectedReviewId(null);
		void navigate({
			to: '/wizard/admin/eval/operator/$userId',
			params: { userId },
			search: (prev) => ({ ...prev, kind: kind === 'training' ? undefined : kind }),
			replace: true,
		});
	}

	return (
		<div>
			<nav style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#666' }}>
				<Link to='/wizard/admin/eval' style={{ color: '#3b82f6' }}>
					← All operators
				</Link>
			</nav>
			<h2 style={{ margin: '0 0 1rem', fontSize: '1.25rem' }}>{operatorName}</h2>
			<EvalDashboard
				plans={plans}
				heatmapRows={heatmapRows}
				operators={operators}
				failedEvals={failedEvals}
				operatorIds={[userId]}
				onRowClick={handleRowClick}
				selectedDetail={selectedDetail}
				onRescore={handleRescore}
				rescoring={rescoreMutation.isPending}
				evalKind={activeKind}
				onEvalKindChange={handleKindChange}
			/>
		</div>
	);
}
